import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Clock, Shield, Award } from 'lucide-react';
import { businessInfo, services } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1a1a1a] text-white">
      {/* Google Map Section */}
      <div className="bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4">Visit Our Location</h3>
              <p className="text-gray-300 mb-4">
                Located on Kermit Highway in Odessa, we serve the entire Permian Basin 
                with fast, professional plumbing services.
              </p>
              <a 
                href="https://www.google.com/maps/dir//Basin+Plumbing,+3730+Kermit+Hwy,+Odessa,+TX+79764"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#e85a24] hover:text-[#ff6b35] font-medium transition-colors"
              >
                <MapPin size={18} />
                Get Directions on Google Maps
              </a>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <iframe
                title="Basin Plumbing Location - Odessa TX"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3382.8!2d-102.4252!3d31.8673!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86fbc7c0baf766d5%3A0x9959fe1a1d46471!2sBasin%20Plumbing!5e0!3m2!1sen!2sus!4v1707847200000!5m2!1sen!2sus"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/basin-plumbing-logo.png" 
                alt="Basin Plumbing Logo" 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Your trusted 24/7 emergency plumber serving Odessa, Midland, and the Permian Basin since 2015.
            </p>
            <div className="flex gap-3">
              <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="text-[#e85a24]" size={24} />
              </div>
              <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center">
                <Award className="text-[#e85a24]" size={24} />
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#e85a24]"></span>
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.slice(0, 7).map((service) => (
                <li key={service.id}>
                  <Link 
                    to={`/services/${service.slug}`}
                    className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#e85a24]"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/faqs" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  FAQ's
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/plumbers-in-midland" className="text-gray-400 hover:text-[#e85a24] hover:pl-2 transition-all duration-200 block">
                  Midland Plumbers
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-[#e85a24]"></span>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="https://www.google.com/maps/dir//Basin+Plumbing,+3730+Kermit+Hwy,+Odessa,+TX+79764"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <MapPin size={20} className="mt-1 flex-shrink-0 group-hover:text-[#e85a24]" />
                  <span>{businessInfo.address.full}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <Phone size={20} className="flex-shrink-0 group-hover:text-[#e85a24]" />
                  <span className="font-semibold text-white">{businessInfo.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <Mail size={20} className="flex-shrink-0 group-hover:text-[#e85a24]" />
                  <span>{businessInfo.email}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Clock size={20} className="flex-shrink-0 text-[#e85a24]" />
                <span>24/7 Emergency Service</span>
              </li>
            </ul>
            <div className="mt-6">
              <a 
                href={businessInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#e85a24] px-4 py-2 rounded-lg transition-colors"
              >
                <Facebook size={20} />
                <span>Follow Us</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-center text-gray-500 text-sm">
            <strong className="text-gray-400">Service Areas:</strong>{' '}
            {businessInfo.serviceAreas.join(' • ')}
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-center text-gray-500 text-sm">
            © {currentYear} Basin Plumbing. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
